// ============================================================================
// INDEXEDDB BUNDLE STATE MANAGEMENT FUNCTIONS
// ============================================================================

const BUNDLE_DB_NAME = 'BundleCustomizer';
const BUNDLE_DB_VERSION = 1;
const BUNDLE_STORE_NAME = 'bundles';
const BUNDLE_KEY = 'bundleCustomizerV1';

/**
 * Initialize IndexedDB for bundle storage
 * Creates database and object store if they don't exist
 */
const initBundleDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(BUNDLE_DB_NAME, BUNDLE_DB_VERSION);
    
    request.onerror = () => {
      console.error('IndexedDB error:', request.error);
      reject(request.error);
    };
    
    request.onsuccess = () => {
      resolve(request.result);
    };
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(BUNDLE_STORE_NAME)) {
        db.createObjectStore(BUNDLE_STORE_NAME);
      }
    };
  });
};

/**
 * Get a reference to the bundle object store
 */
const getBundleStore = async (mode = 'readonly') => {
  const db = await initBundleDB();
  const transaction = db.transaction(BUNDLE_STORE_NAME, mode);
  return transaction.objectStore(BUNDLE_STORE_NAME);
};

/**
 * Retrieve the complete bundle state from IndexedDB
 * Returns the full bundle object or null if not initialized
 */
const getBundleState = async () => {
  try {
    const store = await getBundleStore('readonly');
    return new Promise((resolve, reject) => {
      const request = store.get(BUNDLE_KEY);
      
      request.onerror = () => {
        console.error('Error reading bundle state:', request.error);
        reject(request.error);
      };
      
      request.onsuccess = () => {
        resolve(request.result || null);
      };
    });
  } catch(e) {
    console.error('Error reading bundle state:', e);
    return null;
  }
};

/**
 * Save the complete bundle state to IndexedDB
 * Ensures the entire state object is properly serialized
 */
const saveBundleState = async (state) => {
  try {
    if (!state || !state.isBundle) {
      console.warn('Invalid bundle state to save');
      return;
    }
    const store = await getBundleStore('readwrite');
    return new Promise((resolve, reject) => {
      const request = store.put(state, BUNDLE_KEY);
      
      request.onerror = () => {
        console.error('Error saving bundle state:', request.error);
        reject(request.error);
      };
      
      request.onsuccess = () => {
        resolve();
      };
    });
  } catch(e) {
    console.error('Error saving bundle state:', e);
  }
};

/**
 * Initialize bundle state if not already set
 * Creates a new bundle structure with empty items for each bundle index
 * Only initializes if bundle doesn't already exist
 */
const initBundleStateIfNeeded = async (bundleSize = 3) => {
  let s = await getBundleState();
  if (!s || !s.isBundle) {
    s = {
      isBundle: true,
      bundleSize: bundleSize,
      currentIndex: 0,
      items: Array.from({length: bundleSize}, () => ({}))
    };
    await saveBundleState(s);
  }
  return s;
};

/**
 * Clear all bundle state from IndexedDB
 * This should be called after successfully adding bundle to cart
 */
const clearBundleState = async () => {
  try {
    const store = await getBundleStore('readwrite');
    return new Promise((resolve, reject) => {
      const request = store.delete(BUNDLE_KEY);
      
      request.onerror = () => {
        console.error('Error clearing bundle state:', request.error);
        reject(request.error);
      };
      
      request.onsuccess = () => {
        // console.log('reset bundle state');
        resolve();
      };
    });
  } catch(e) {
    console.error('Error clearing bundle state:', e);
  }
};

/**
 * Update the bundle UI to show current progress
 * Displays which bundle item is being customized (e.g., "Customizing 1 of 3")
 */
const updateBundleUI = (index, size) => {
  const infoEl = document.querySelectorAll('.cover-step-info');
  if (!infoEl.length) return; // Element may not exist
  
  infoEl.forEach(currentInfoEl => {
    if (index === null || index === undefined) {
      currentInfoEl.textContent = ''; // Clear if no active index
      return;
    }
    
    currentInfoEl.textContent = `Customizing Book: ${index + 1} of ${size}`;
  });
};


/**
 * Create a complete snapshot of the current cover customization
 * This captures all relevant state for a bundle item
 */
const snapshotCurrentCover = () => {
  // Update customBook with current form values
  if (typeof window.updateCustomBook === 'function') {
    window.updateCustomBook();
  }
  
  const customBook = window.customBook || {};
  
  // Create snapshot with all relevant fields
  const snapshot = {
    // Text fields
    title: customBook.title || '',
    subTitle: customBook.subTitle || '',
    subTitle2: customBook.subTitle2 || '',
    
    // Font configuration
    font: customBook.font ? Object.assign({}, customBook.font) : {},
    
    // Colors
    textColor: customBook.textColor || '',
    subTitleColor: customBook.subTitleColor || '',
    bgColor: customBook.bgColor || '',
    svgColor: customBook.svgColor || '',
    
    // Style
    style: customBook.currentStyleIndex || customBook.style || 1,

    // Image data (base64 strings - safe to serialize)
    uploadedImageFullSizeDataURL: customBook.uploadedImageFullSizeDataURL || '',
    
    // Canvas preview
    canvasDataURL: '',
    
    // Variant ID for add-to-cart
    variantId: ''
  };
  
  // Capture canvas preview
  const canvasEl = document.getElementById('coverCanvas');
  if (canvasEl && typeof canvasEl.toDataURL === 'function') {
    try {
      snapshot.canvasDataURL = canvasEl.toDataURL('image/png');
    } catch(e) {
      console.warn('Failed to capture canvas preview:', e);
      snapshot.canvasDataURL = '';
    }
  }
  
  // Capture variant ID
  const variantInput = document.querySelector('form.closer-books-customizer-form input[name="id"]');
  if (variantInput && variantInput.value) {
    snapshot.variantId = variantInput.value;
  }
  
  return snapshot;
};

/**
 * Apply a snapshot to the form and canvas
 * Restores all fields, images, and canvas state for a specific bundle item
 * Ensures no data leakage between bundles
 */
const applySnapshotToForm = (snapshot) => {
  if (!snapshot) {
    console.warn('No snapshot provided to applySnapshotToForm');
    return;
  }
  const customBook = window.customBook || {};
  
  // === Apply Text Fields ===
  const titleInput = document.getElementById('titleInput');
  const subTitleInput = document.getElementById('subTitleInput');
  
  if (titleInput) titleInput.value = snapshot.title || '';
  if (subTitleInput) subTitleInput.value = snapshot.subTitle || '';
  
  // === Apply Font Selection ===
  const fontSelector = document.getElementById('fontSelector');
  const subTitleFontSelector = document.getElementById('subTitleFontSelector');
  
  if (fontSelector && snapshot.font && snapshot.font.titleFonts) {
    fontSelector.value = snapshot.font.titleFonts;
  }
  if (subTitleFontSelector && snapshot.font && snapshot.font.subTitleFonts) {
    subTitleFontSelector.value = snapshot.font.subTitleFonts;
  }

  const nativeTitleFontSelect = document.getElementById("fontSelector");
  const titleDropdown = document.getElementById("fontDropdown");
  const nativeSubTitleFontSelect = document.getElementById("subTitleFontSelector");
  const subTitleDropdown = document.getElementById("subTitleFontDropdown");

  if (nativeTitleFontSelect && titleDropdown) {
    const selectedBox = titleDropdown.querySelector('.custom-selected');
    const currentFont = nativeTitleFontSelect.value;
    const currentOption = titleDropdown.querySelector(`.font-option[data-value="${currentFont}"]`);
    if (currentOption) {
      selectedBox.textContent = currentOption.textContent;
      selectedBox.style.fontFamily = currentOption.style.fontFamily;
    }
  }
  if (nativeSubTitleFontSelect && subTitleDropdown) {
    const selectedBox = subTitleDropdown.querySelector('.custom-selected');
    const currentFont = nativeSubTitleFontSelect.value;
    const currentOption = subTitleDropdown.querySelector(`.font-option[data-value="${currentFont}"]`);
    if (currentOption) {
      selectedBox.textContent = currentOption.textContent;
      selectedBox.style.fontFamily = currentOption.style.fontFamily;
    }
  }
  
  // === Apply Colors ===
  const textColorPicker = document.getElementById('textColorPicker');
  const subTitleColorPicker = document.getElementById('subTitleColorPicker');
  const bgColorPicker = document.getElementById('bgColorPicker');
  const svgColorPicker = document.getElementById('svgColorPicker');
  
  if (textColorPicker && snapshot.textColor) textColorPicker.value = snapshot.textColor;
  if (subTitleColorPicker && snapshot.subTitleColor) subTitleColorPicker.value = snapshot.subTitleColor;
  if (bgColorPicker && snapshot.bgColor) bgColorPicker.value = snapshot.bgColor;
  if (svgColorPicker && snapshot.svgColor) svgColorPicker.value = snapshot.svgColor;

  // also update color preview swatches with proper applyColorPreview functions
  if (typeof window.applyColorPreview === 'function') {
    if (textColorPicker && snapshot.textColor) {
      window.applyColorPreview(textColorPicker, snapshot.textColor);
    }
    if (subTitleColorPicker && snapshot.subTitleColor) {
      window.applyColorPreview(subTitleColorPicker, snapshot.subTitleColor);
    }
    if (bgColorPicker && snapshot.bgColor) {
      window.applyColorPreview(bgColorPicker, snapshot.bgColor);
    }
    if (svgColorPicker && snapshot.svgColor) {
      window.applyColorPreview(svgColorPicker, snapshot.svgColor);
    }
  }
  
  
  // === Apply Style ===
  customBook.currentStyleIndex = snapshot.style || customBook.currentStyleIndex || 1;
  
  // === Restore Uploaded Images ===
  window.resetUploadedImageUsingUrl(snapshot.uploadedImageFullSizeDataURL);
  // First, restore full-size image (used for download/sending)
  if (snapshot.uploadedImageFullSizeDataURL) {
    const fullImg = new Image();
    fullImg.crossOrigin = 'anonymous';
    fullImg.onload = function() {
      customBook.uploadedImageFullSize = fullImg;
      const fullSizeEl = document.getElementById('uploadedImageFullSize');
      if (fullSizeEl) fullSizeEl.src = snapshot.uploadedImageFullSizeDataURL;
    };
    fullImg.onerror = function() {
      console.warn('Failed to load full-size image from snapshot');
      customBook.uploadedImageFullSize = null;
    };
    fullImg.src = snapshot.uploadedImageFullSizeDataURL;
  } else {
    customBook.uploadedImageFullSize = null;
    const fullSizeEl = document.getElementById('uploadedImageFullSize');
    if (fullSizeEl) fullSizeEl.src = '';
  }
  
  // Second, restore display/preview image (used for canvas rendering)
  if (snapshot.uploadedImageFullSizeDataURL) {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function() {
      customBook.uploadedImage = img;
      customBook.uploadedImageFullSizeDataURL = snapshot.uploadedImageFullSizeDataURL;
      
      // Update preview image element
      const previewEl = document.querySelector('[data-uploadedImage-container] #userPreview');
      if (previewEl) previewEl.src = snapshot.uploadedImageFullSizeDataURL;
      
      // Redraw canvas with the restored image
      if (typeof window.drawCover === 'function') {
        window.drawCover();
      }
    };
    img.onerror = function() {
      console.warn('Failed to load image from snapshot');
      customBook.uploadedImage = null;
      customBook.uploadedImageFullSizeDataURL = '';
    };
    img.src = snapshot.uploadedImageFullSizeDataURL;
  } else {
    // No image in this snapshot - clear image state
    customBook.uploadedImage = null;
    customBook.uploadedImageFullSizeDataURL = '';
    customBook.uploadedImageFullSize = null;
    
    const previewEl = document.querySelector('[data-uploadedImage-container] #userPreview');
    if (previewEl) previewEl.src = '';
    const fullSizeEl = document.getElementById('uploadedImageFullSize');
    if (fullSizeEl) fullSizeEl.src = '';
    
    // Reset to blank canvas
    if (typeof window.createResetPreviewImage === 'function') {
      window.createResetPreviewImage();
    }
  }
  // === Restore Variant ID ===
  const variantInput = document.querySelector('form.closer-books-customizer-form input[name="id"]');
  if (variantInput && snapshot.variantId) {
    variantInput.value = snapshot.variantId;
  }
  
  // === Update customBook and redraw ===
  if (typeof window.updateCustomBook === 'function') {
    window.updateCustomBook();
  }
  
  if (typeof window.changeCanvas === 'function') {
    window.changeCanvas();
  }
};

//make applyColorPreview function
if (typeof window.applyColorPreview !== 'function') {
  window.applyColorPreview = (colorPicker, color) => {
    // console.log('Applying color preview:',colorPicker ,  color);
    const previewSwatch = colorPicker.closest('.color-picker-wrapper').querySelector('.preview-custom-color-swatch');
    // console.log('Applying color swatch :',previewSwatch);

    if (previewSwatch) {
      previewSwatch.style.background = color;
    }
    
    // also update the color-palette's custom-color-swatch selected which has the data-hex attribute same as the color
    if (previewSwatch) {
      const swatches = previewSwatch.closest('.color-picker-wrapper').querySelectorAll('.color-palette .custom-color-swatch');      
      swatches.forEach(swatch => {
        console.log('Checking swatch :',swatch.getAttribute('data-hex'), color.toUpperCase());
        if (swatch.getAttribute('data-hex') === color.toUpperCase()) {
          swatch.classList.add('selected');
        } else {
          swatch.classList.remove('selected');
        }
      });
    }
    
  };
} 